import { dataOf, nodeTree } from "./document.js";

export const EDITABLE_TARGET_KINDS = ["Activation", "Transform", "Material"];

export function stateTargetRefs(set) {
    const data = dataOf(set) ?? {};
    switch (set.kind) {
        case "Activation": return [...new Set(set.options.flatMap(option => option.raw.TargetPaths ?? []))].map(Path => ({ Path }));
        case "Transform": return typeof data.TargetTransformPath === "string" ? [{ Path: data.TargetTransformPath }] : [];
        case "Material": return data.TargetRenderers?.length ? data.TargetRenderers.map(ref => ({ ...ref })) : [{ Path: set.raw.Path ?? "" }];
        case "Camera": return [{ Path: data.ReferenceTransformPath ?? set.raw.Path ?? "" }];
        case "Light": return (data.TargetLightPaths ?? []).map(Path => ({ Path }));
        case "Animation": return [{ Path: data.TargetAnimationPath || set.raw.Path || "" }];
        default: return [];
    }
}

export function targetRefsForNodes(doc, kind, indices) {
    if (!EDITABLE_TARGET_KINDS.includes(kind)) throw new Error("Targets for this State type are read-only in web Create.");
    const nodes = nodeTree(doc.json), unique = [...new Set(indices)];
    if (!unique.length) throw new Error("Select a part in the hierarchy first.");
    if (kind === "Transform" && unique.length !== 1) throw new Error("A Transform State needs exactly one target. Drag or select one part.");
    return unique.map(index => {
        const node = nodes.find(candidate => candidate.index === index);
        if (!node || node.path.split("/").some(segment => segment.startsWith("__INNOWORKS_MATERIAL"))) throw new Error("This part cannot be used as a target.");
        if (nodes.filter(candidate => candidate.path === node.path).length !== 1) throw new Error("This part has an ambiguous hierarchy path. Rename duplicate siblings before targeting it.");
        if (kind === "Material" && node.mesh === undefined) throw new Error("Material States need mesh parts. Select a mesh in the hierarchy.");
        return kind === "Material" ? { Path: node.path, ComponentType: "UnityEngine.Renderer, UnityEngine.CoreModule" } : { Path: node.path };
    });
}

// Work on the pending document so removing a target stays reversible until Update.
export function applyStateTargets(set, refs) {
    if (!EDITABLE_TARGET_KINDS.includes(set.kind)) return;
    if (!refs.length) throw new Error("Add at least one target before updating this State.");
    if (set.kind === "Transform") {
        if (refs.length !== 1) throw new Error("A Transform State needs exactly one target.");
        dataOf(set).TargetTransformPath = refs[0].Path;
    } else if (set.kind === "Material") {
        dataOf(set).TargetRenderers = refs.map(ref => ({ ...ref }));
    } else {
        const allowed = new Set(refs.map(ref => ref.Path));
        set.options.forEach(option => { option.raw.TargetPaths = (option.raw.TargetPaths ?? []).filter(path => allowed.has(path)); });
        if (!set.options.some(option => option.raw.TargetPaths.length)) throw new Error("Show at least one target in a Visibility option before updating.");
    }
}
